import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { ClientId } from "../../domain/types";
import { useCoClient } from "../../data/store";
import {
  tallyPartyApi,
  type TallyParty,
  type TallyPartyImport,
  type TallyPartyImportRow,
  type TallyPartyState,
} from "../../data/tallyPartyApi";
import { Callout } from "../../components/Callout";
import { CoButton } from "../../components/Button";
import { DownloadIcon, SearchIcon, SheetIcon, UploadIcon } from "../../components/icons";
import { StatusCard, type CardStatus } from "../../components/StatusCard";
import { SubStepShell } from "../../components/SubStepShell";
import { cn } from "../../components/cn";
import { useSubStepNav } from "../useSubStepNav";

/**
 * Customers and vendors read from Tally.
 *
 * Same screen shape as the Zoho step: a count bar, Customers / Vendors / Upload,
 * and a sheet card per side. There is no Exceptions view. A party already in
 * Tally is left as Tally has it; the sheet only adds names, and a bad row is
 * refused on upload rather than listed as a standing problem.
 */

type Party = "customer" | "vendor";
type TopTab = Party | "upload";

function sheetStatus(imported: TallyPartyImport | null): CardStatus {
  if (imported === null) return "not-started";
  if (imported.invalid_rows > 0 || imported.failed_rows > 0) return "needs-attention";

  return "uploaded";
}

function attentionRows(imported: TallyPartyImport | null): number {
  if (imported === null) return 0;

  return imported.invalid_rows + imported.failed_rows;
}

export function TallyCustomersVendorsStep({ clientId }: { clientId: ClientId }) {
  const client = useCoClient(clientId);
  const { goNext } = useSubStepNav(clientId, "customers-vendors");
  const [state, setState] = useState<TallyPartyState | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [tab, setTab] = useState<TopTab>("customer");
  const [search, setSearch] = useState("");
  // Bumped on every read so a slow empty response cannot overwrite a later one.
  // Strict Mode starts the effect twice, and the poll used to apply whichever
  // reply arrived last — that reply was often the table mid-refresh, so the
  // step stayed on 0 until a full reload.
  const requestSeq = useRef(0);
  const sawCommitting = useRef(false);
  const emptyReads = useRef(0);

  const load = useCallback(() => {
    const seq = ++requestSeq.current;
    tallyPartyApi
      .state(clientId)
      .then((next) => {
        if (seq !== requestSeq.current) return;
        setState(next);
        setLoadError(null);
      })
      .catch((reason: unknown) => {
        if (seq !== requestSeq.current) return;
        setLoadError(reason instanceof Error ? reason.message : "Could not load customers and vendors.");
      });
  }, [clientId]);

  // A sheet upload writes the list itself. Invalidate any read still in flight
  // so that reply cannot put the empty snapshot back on screen.
  const replaceState = useCallback((next: TallyPartyState) => {
    requestSeq.current += 1;
    setState(next);
    setLoadError(null);
  }, []);

  useEffect(() => {
    load();
    return () => {
      requestSeq.current += 1;
    };
  }, [load]);

  const committing =
    state?.imports.customer?.status === "committing" || state?.imports.vendor?.status === "committing";

  useEffect(() => {
    if (!committing) return undefined;
    const timer = window.setInterval(load, 2000);
    return () => window.clearInterval(timer);
  }, [committing, load]);

  // The import flips to done in the same moment Tally's read replaces the
  // party rows. Read once more after that, or the screen keeps the empty
  // snapshot from just before the insert.
  useEffect(() => {
    if (committing) {
      sawCommitting.current = true;
      return;
    }
    if (!sawCommitting.current) return;
    sawCommitting.current = false;
    load();
  }, [committing, load]);

  // A company with no parties stays empty. A company whose read landed a
  // moment after this step opened should not. Two follow-ups, then stop.
  useEffect(() => {
    if (state === null || committing) return undefined;
    const empty = state.customers.length === 0 && state.vendors.length === 0;
    if (!empty) {
      emptyReads.current = 0;
      return undefined;
    }
    if (emptyReads.current >= 2) return undefined;
    const timer = window.setTimeout(() => {
      emptyReads.current += 1;
      load();
    }, 1000);
    return () => window.clearTimeout(timer);
  }, [state, committing, load]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") load();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [load]);

  const clientName = client?.name || "this client";
  const description =
    `Below are the customers and vendors currently available in ${clientName}'s Tally company. ` +
    "Zournal has read these from Tally. A sheet adds new ones. A name that is already there is left as Tally has it.";

  if (loadError !== null && state === null) {
    return (
      <SubStepShell
        title="Set Up Customers & Vendors"
        description={description}
        thenWhat="The contacts have to load before this step can be signed off."
        onContinue={() => void goNext()}
        continueDisabled
      >
        <Callout variant="warning">{loadError}</Callout>
      </SubStepShell>
    );
  }

  if (state === null) {
    return (
      <SubStepShell
        title="Set Up Customers & Vendors"
        description={description}
        thenWhat="The contacts have to load before this step can be signed off."
        onContinue={() => void goNext()}
        continueDisabled
      >
        <p className="text-sm text-muted-foreground">Reading the contacts…</p>
      </SubStepShell>
    );
  }

  const customers = state.customers.length;
  const vendors = state.vendors.length;
  const customerSheet = sheetStatus(state.imports.customer);
  const vendorSheet = sheetStatus(state.imports.vendor);
  const sheetsNeedingAttention =
    (customerSheet === "needs-attention" ? 1 : 0) + (vendorSheet === "needs-attention" ? 1 : 0);
  const totalAttention = attentionRows(state.imports.customer) + attentionRows(state.imports.vendor);
  const ready = !committing && sheetsNeedingAttention === 0;
  const holdup = committing
    ? "Wait for Tally to finish creating these parties."
    : sheetsNeedingAttention > 0
      ? "Fix the refused or invalid rows on the Upload tab to continue."
      : "Continue → Set Up Opening Balances.";

  const party: Party = tab === "vendor" ? "vendor" : "customer";

  return (
    <SubStepShell
      title="Set Up Customers & Vendors"
      description={description}
      thenWhat={holdup}
      onContinue={() => void goNext()}
      continueDisabled={!ready}
    >
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-muted/20 p-4">
        <p className="text-sm font-medium">
          {customers} {customers === 1 ? "customer" : "customers"} · {vendors}{" "}
          {vendors === 1 ? "vendor" : "vendors"}
        </p>
        {totalAttention > 0 ? (
          <span className="rounded-full border border-amber-500/40 bg-amber-500/10 px-2.5 py-1 text-xs font-medium text-amber-700 dark:text-amber-400">
            {totalAttention} need attention
          </span>
        ) : (
          <span className="rounded-full border bg-muted/40 px-2.5 py-1 text-xs font-medium text-muted-foreground">
            Nothing needs attention
          </span>
        )}
      </div>

      <TopTabs
        tab={tab}
        onChange={setTab}
        customers={customers}
        vendors={vendors}
        sheetsNeedingAttention={sheetsNeedingAttention}
      />

      {tab !== "upload" && (
        <PartyView party={party} rows={party === "vendor" ? state.vendors : state.customers} search={search} onSearch={setSearch} onUpload={() => setTab("upload")} />
      )}

      {/* Kept mounted so a sheet review stays put while the list is open. */}
      <div className={cn("flex flex-col gap-4", tab !== "upload" && "hidden")}>
        <p className="text-sm text-muted-foreground">
          Want to add new customers or vendors? Download the relevant sheet, add the new rows, and upload the
          completed sheet back. A name already in Tally is left as Tally has it.
        </p>

        <StatusCard
          title="Customer sheet"
          description={`Download ${clientName}'s customers, add new ones in Excel, and upload the sheet back.`}
          status={customerSheet}
        >
          <PartySheet
            clientId={clientId}
            party="customer"
            clientName={clientName}
            imported={state.imports.customer}
            onState={replaceState}
            onRefresh={load}
          />
        </StatusCard>

        <StatusCard
          title="Vendor sheet"
          description={`Download ${clientName}'s vendors, add new ones in Excel, and upload the sheet back.`}
          status={vendorSheet}
        >
          <PartySheet
            clientId={clientId}
            party="vendor"
            clientName={clientName}
            imported={state.imports.vendor}
            onState={replaceState}
            onRefresh={load}
          />
        </StatusCard>
      </div>
    </SubStepShell>
  );
}

function Tab({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active}
      className={cn(
        "flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium transition-colors",
        active ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"
      )}
    >
      {children}
    </button>
  );
}

function TopTabs({
  tab,
  onChange,
  customers,
  vendors,
  sheetsNeedingAttention,
}: {
  tab: TopTab;
  onChange: (tab: TopTab) => void;
  customers: number;
  vendors: number;
  sheetsNeedingAttention: number;
}) {
  return (
    <div className="flex items-center gap-1 border-b">
      {(
        [
          ["customer", "Customers", customers],
          ["vendor", "Vendors", vendors],
        ] as const
      ).map(([value, label, count]) => (
        <Tab key={value} active={tab === value} onClick={() => onChange(value)}>
          {label}
          <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
            {count}
          </span>
        </Tab>
      ))}
      <Tab active={tab === "upload"} onClick={() => onChange("upload")}>
        Upload
        {sheetsNeedingAttention > 0 && (
          <span className="rounded-full bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 dark:text-amber-400">
            {sheetsNeedingAttention}
          </span>
        )}
      </Tab>
    </div>
  );
}

function PartyView({
  party,
  rows,
  search,
  onSearch,
  onUpload,
}: {
  party: Party;
  rows: TallyParty[];
  search: string;
  onSearch: (value: string) => void;
  onUpload: () => void;
}) {
  const noun = party === "vendor" ? "vendors" : "customers";
  const listed = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (query === "") return rows;

    return rows.filter(
      (row) => row.name.toLowerCase().includes(query) || (row.gstin ?? "").toLowerCase().includes(query)
    );
  }, [rows, search]);

  return (
    <>
      <div className="flex justify-end">
        <label className="relative flex w-full min-w-52 items-center sm:max-w-xs">
          <SearchIcon className="pointer-events-none absolute left-2.5 size-4 text-muted-foreground" />
          <input
            value={search}
            onChange={(event) => onSearch(event.target.value)}
            placeholder="Search by name or GSTIN"
            className="h-9 w-full rounded-md border bg-background pl-8 pr-3 text-sm"
          />
        </label>
      </div>

      {listed.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {search.trim() !== ""
            ? `No ${noun} match "${search.trim()}".`
            : `No ${noun} have been read from Tally yet.`}
        </p>
      ) : (
        <div className="flex flex-col gap-2">
          <p className="text-sm text-muted-foreground">
            {listed.length} {listed.length === 1 ? noun.replace(/s$/, "") : noun}
          </p>
          <ul className="flex max-h-[32rem] flex-col gap-1.5 overflow-y-auto">
            {listed.map((row) => (
              <li
                key={row.guid ?? `${row.name}:${row.gstin ?? ""}`}
                className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border px-3 py-2 text-sm"
              >
                <span className="min-w-0 flex-1 truncate font-medium">{row.name}</span>
                <span className="font-mono text-xs text-muted-foreground">{row.gstin ?? "No GSTIN"}</span>
                <span className="w-16 shrink-0 text-xs text-muted-foreground">{row.currency_code ?? ""}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <p className="text-sm text-muted-foreground">
        To add a contact, download the sheet from the{" "}
        <button type="button" onClick={onUpload} className="underline underline-offset-2 hover:text-foreground">
          Upload tab
        </button>
        , add the row, and upload it back.
      </p>
    </>
  );
}

function PartySheet({
  clientId,
  party,
  clientName,
  imported,
  onState,
  onRefresh,
}: {
  clientId: ClientId;
  party: Party;
  clientName: string;
  imported: TallyPartyImport | null;
  onState: (state: TallyPartyState) => void;
  onRefresh: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<"download" | "upload" | "commit" | "discard" | null>(null);
  const [dragging, setDragging] = useState(false);
  const noun = party === "vendor" ? "vendors" : "customers";
  const committing = imported?.status === "committing";

  async function run(kind: "upload" | "commit" | "discard", work: () => Promise<TallyPartyState>) {
    setPending(kind);
    setError(null);
    try {
      onState(await work());
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "That did not work.");
      onRefresh();
    } finally {
      setPending(null);
    }
  }

  function download() {
    setPending("download");
    setError(null);
    tallyPartyApi
      .template(clientId, party)
      .catch((reason: unknown) => {
        setError(reason instanceof Error ? reason.message : "Could not download the sheet.");
      })
      .finally(() => setPending(null));
  }

  function upload(file: File) {
    void run("upload", () => tallyPartyApi.upload(clientId, party, file));
  }

  const locked = pending !== null || committing;

  return (
    <section className="flex flex-col gap-4 rounded-lg border p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h3 className="text-base font-medium">Bulk upload {noun}</h3>
          <p className="text-sm text-muted-foreground">
            Download {clientName}&apos;s {noun}, add new ones in Excel, and upload the sheet back. A name already in
            Tally is left as Tally has it.
          </p>
        </div>
        <CoButton variant="outline" onClick={download} disabled={locked}>
          <DownloadIcon className="size-4" />
          Download sheet
        </CoButton>
      </div>

      {error !== null && <Callout variant="warning">{error}</Callout>}

      {imported === null ? (
        <DropSheet dragging={dragging} reading={pending === "upload"} disabled={locked} onDragging={setDragging} onFile={upload} />
      ) : (
        <SheetReview
          imported={imported}
          pending={pending}
          disabled={locked}
          onCommit={() => void run("commit", () => tallyPartyApi.commit(clientId, party))}
          onDiscard={() => void run("discard", () => tallyPartyApi.cancel(clientId, party))}
          onFile={upload}
        />
      )}
    </section>
  );
}

function DropSheet({
  dragging,
  reading,
  disabled,
  onDragging,
  onFile,
}: {
  dragging: boolean;
  reading: boolean;
  disabled: boolean;
  onDragging: (dragging: boolean) => void;
  onFile: (file: File) => void;
}) {
  return (
    <label
      onDragOver={(event) => {
        event.preventDefault();
        onDragging(true);
      }}
      onDragLeave={() => onDragging(false)}
      onDrop={(event) => {
        event.preventDefault();
        onDragging(false);
        const file = event.dataTransfer.files[0];
        if (file) onFile(file);
      }}
      className={cn(
        "relative flex cursor-pointer flex-col items-center gap-1.5 rounded-md border border-dashed p-6 text-center transition-colors",
        dragging ? "border-primary bg-primary/5" : "hover:bg-muted/40",
        disabled && "pointer-events-none opacity-70"
      )}
    >
      <UploadIcon className="size-5 text-muted-foreground" />
      <span className="text-sm font-medium">
        {reading ? "Reading the sheet…" : "Drop the filled-in sheet here, or click to choose"}
      </span>
      <span className="text-xs text-muted-foreground">.xlsx or .csv</span>
      <input
        type="file"
        accept=".xlsx,.csv"
        className="sr-only"
        disabled={disabled}
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = "";
          if (file) onFile(file);
        }}
      />
    </label>
  );
}

const ACTION_LABEL: Record<TallyPartyImportRow["action"], string> = {
  new: "To be added",
  invalid: "Needs fixing",
  unchanged: "Already in Tally",
};

const ACTION_STYLE: Record<TallyPartyImportRow["action"], string> = {
  new: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
  invalid: "bg-amber-500/10 text-amber-700 dark:text-amber-400",
  unchanged: "bg-muted text-muted-foreground",
};

function SheetReview({
  imported,
  pending,
  disabled,
  onCommit,
  onDiscard,
  onFile,
}: {
  imported: TallyPartyImport;
  pending: "download" | "upload" | "commit" | "discard" | null;
  disabled: boolean;
  onCommit: () => void;
  onDiscard: () => void;
  onFile: (file: File) => void;
}) {
  const settled = imported.status === "done" || imported.status === "committing";
  const canCreate =
    imported.status === "validated" && imported.invalid_rows === 0 && imported.new_rows > 0;
  const canDiscard = imported.status === "validated";

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <SheetIcon className="size-4 shrink-0 text-muted-foreground" />
        <span className="font-medium">{imported.filename ?? "Sheet"}</span>
        <span className="text-xs text-muted-foreground">{imported.total_rows} rows read</span>
      </div>

      {settled ? (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          <Tile label="Created" count={imported.created_rows} style={ACTION_STYLE.new} />
          <Tile label="Failed" count={imported.failed_rows} style={ACTION_STYLE.invalid} />
          <Tile label="Already in Tally" count={imported.unchanged_rows} style={ACTION_STYLE.unchanged} />
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          <Tile label={ACTION_LABEL.new} count={imported.new_rows} style={ACTION_STYLE.new} />
          <Tile label={ACTION_LABEL.invalid} count={imported.invalid_rows} style={ACTION_STYLE.invalid} />
          <Tile label={ACTION_LABEL.unchanged} count={imported.unchanged_rows} style={ACTION_STYLE.unchanged} />
        </div>
      )}

      {imported.status === "committing" && (
        <p className="text-sm text-muted-foreground">Creating them in Tally…</p>
      )}
      {imported.status === "validated" && imported.new_rows === 0 && imported.invalid_rows === 0 && (
        <p className="text-sm text-muted-foreground">Nothing in this sheet would be created in Tally.</p>
      )}
      {imported.invalid_rows > 0 && (
        <Callout variant="warning">
          {imported.invalid_rows} {imported.invalid_rows === 1 ? "row needs" : "rows need"} fixing before{" "}
          {imported.invalid_rows === 1 ? "it goes" : "they go"} to Tally. Fix those lines in the sheet and upload it
          again.
        </Callout>
      )}
      {imported.status === "done" && imported.failed_rows > 0 && (
        <Callout variant="warning">
          {imported.failed_rows} {imported.failed_rows === 1 ? "row was" : "rows were"} refused. Fix{" "}
          {imported.failed_rows === 1 ? "it" : "them"} in the sheet and upload it again.
        </Callout>
      )}

      <SheetRows rows={imported.rows} showOutcome={settled} />

      <div className="flex flex-wrap items-center gap-2 border-t pt-4">
        {canCreate && (
          <CoButton type="button" disabled={disabled} onClick={onCommit}>
            {pending === "commit" ? "Creating…" : `Create ${imported.new_rows} in Tally`}
          </CoButton>
        )}
        {canDiscard && (
          <CoButton type="button" variant="outline" disabled={disabled} onClick={onDiscard}>
            {pending === "discard" ? "Discarding…" : "Discard"}
          </CoButton>
        )}
        {imported.status !== "committing" && (
          <label
            className={cn(
              "inline-flex h-8 cursor-pointer items-center justify-center rounded-lg border border-border bg-background px-2.5 text-sm font-medium hover:bg-muted",
              disabled && "pointer-events-none opacity-50"
            )}
          >
            {pending === "upload" ? "Reading…" : "Upload sheet"}
            <input
              type="file"
              accept=".xlsx,.csv"
              className="sr-only"
              disabled={disabled}
              onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = "";
                if (file) onFile(file);
              }}
            />
          </label>
        )}
      </div>
    </div>
  );
}

function Tile({ label, count, style }: { label: string; count: number; style: string }) {
  return (
    <div className={cn("flex flex-col gap-0.5 rounded-md px-3 py-2", style)}>
      <span className="text-xl font-semibold tabular-nums">{count}</span>
      <span className="text-xs font-medium">{label}</span>
    </div>
  );
}

function SheetRows({ rows, showOutcome }: { rows: TallyPartyImportRow[]; showOutcome: boolean }) {
  const [showUnchanged, setShowUnchanged] = useState(false);
  const interesting = rows.filter((row) => row.action !== "unchanged" || row.issues.length > 0 || row.status === "failed");
  const unchanged = rows.filter((row) => row.action === "unchanged" && row.issues.length === 0 && row.status !== "failed");
  const visible = showUnchanged ? rows : interesting;

  if (rows.length === 0) return null;

  return (
    <div className="flex flex-col gap-2 border-t pt-4">
      {visible.length > 0 && (
        <ul className="flex max-h-[28rem] flex-col gap-2 overflow-y-auto">
          {visible.map((row) => (
            <li
              key={row.id}
              className={cn(
                "flex flex-col gap-1 rounded-md border p-3",
                showOutcome && row.status === "failed" && "border-amber-500/40 bg-amber-500/5"
              )}
            >
              <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                <span className="shrink-0 font-mono text-xs text-muted-foreground">Row {row.sheet_row_no}</span>
                <span className="text-sm font-medium">{row.party_name || "(no name)"}</span>
                <span
                  className={cn(
                    "ml-auto shrink-0 rounded-sm px-1.5 py-0.5 text-[11px] font-medium",
                    ACTION_STYLE[row.action]
                  )}
                >
                  {showOutcome && row.status === "created"
                    ? "Created"
                    : showOutcome && row.status === "failed"
                      ? "Tally refused"
                      : ACTION_LABEL[row.action]}
                </span>
              </div>
              {row.issues.map((issue, index) => (
                <p key={index} className="text-xs text-amber-700 dark:text-amber-400">
                  <span className="font-medium">{issue.field}:</span> {issue.message}
                </p>
              ))}
              {row.status === "failed" && row.error && (
                <p className="text-xs text-amber-700 dark:text-amber-400">{row.error}</p>
              )}
            </li>
          ))}
        </ul>
      )}
      {unchanged.length > 0 && (
        <button
          type="button"
          className="self-start text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
          onClick={() => setShowUnchanged((open) => !open)}
        >
          {showUnchanged ? "Hide" : "Show"} {unchanged.length} already in Tally
        </button>
      )}
    </div>
  );
}
